'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const benefits = [
  {
    icon: 'ClockIcon',
    title: 'Economia de Tempo',
    description: 'Automatize tarefas repetitivas e foque no que realmente importa: crescer seu negócio.',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
  },
  {
    icon: 'ShieldCheckIcon',
    title: 'Redução de Erros',
    description: 'Processos automatizados eliminam erros humanos em vendas, estoque e financeiro.',
    color: 'text-secondary',
    bg: 'bg-secondary/10',
    border: 'border-secondary/20',
  },
  {
    icon: 'AdjustmentsHorizontalIcon',
    title: 'Mais Organização',
    description: 'Todas as informações da sua empresa em um só lugar, sempre atualizadas e acessíveis.',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20',
  },
  {
    icon: 'CurrencyDollarIcon',
    title: 'Controle Financeiro',
    description: 'Monitore receitas, despesas, fluxo de caixa e lucro em tempo real com gráficos inteligentes.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/20',
  },
  {
    icon: 'CubeIcon',
    title: 'Controle de Estoque',
    description: 'Alertas de estoque baixo, entradas e saídas automáticas, e relatórios de giro de produtos.',
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/20',
  },
  {
    icon: 'BoltIcon',
    title: 'Mais Produtividade',
    description: 'PDV rápido, atendimento ágil e fluxos otimizados que aumentam a velocidade da sua equipe.',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
  },
  {
    icon: 'ChartBarIcon',
    title: 'Gestão Inteligente',
    description: 'Dashboards com indicadores-chave, comparações mensais e insights acionáveis para decisões.',
    color: 'text-secondary',
    bg: 'bg-secondary/10',
    border: 'border-secondary/20',
  },
  {
    icon: 'ArrowTrendingUpIcon',
    title: 'Crescimento da Empresa',
    description: 'Com dados precisos e controle total, identifique oportunidades e escale seu negócio com confiança.',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20',
  },
];

export default function WhyChooseSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 blob-secondary opacity-50 rounded-full" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-xs font-bold uppercase tracking-widest text-primary mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Por que escolher
          </span>
          <h2 className="text-section-title font-black text-foreground mb-4">
            Por que escolher o{' '}
            <span className="gradient-text">NegócioFácil?</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Mais do que um sistema — uma plataforma que transforma a forma como você gerencia seu negócio.
          </p>
        </div>

        {/* Bento grid */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((benefit, i) => (
            <div
              key={benefit.title}
              className={`glass-card-hover rounded-2xl p-6 card-glow-effect feature-card transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className={`feature-icon-wrap ${benefit.bg} ${benefit.border} mb-4`}>
                <Icon name={benefit.icon as 'ClockIcon'} size={20} className={benefit.color} />
              </div>
              <h3 className="text-base font-bold text-foreground mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}